"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";

import { Card, CardContent } from "@/components/ui/data-display/card";
import SafeImage from "@/components/utils/SafeImage";
import { GetContactsButton } from "@/components/features/reports/GetContactsButton";
import { ApplyComparisonDialog } from "@/components/features/jobs/ApplyComparisonDialog";
import {
  JOB_ACTION_BUTTON_CLASS,
  JOB_ACTION_BUTTON_COMPACT_CLASS,
  JOB_ACTION_BUTTON_ROW_CLASS,
} from "@/components/features/jobs/job-action-button-styles";
import { useEventTracking } from "@/hooks/useEventTracking";
import { useEnhancedTracking } from "@/hooks/useEnhancedTracking";
import { EventType } from "@/api/types/events";
import { generateJobDetailPath } from "@/utils/jobs";
import { stripHtml } from "@/utils/html";
import { cn } from "@/lib/utils";
import { EntityTitle, EntitySubtitle } from "@/components/ui/design-system";

import {
  EntityActionButton,
  EntityBadge,
  EntityMetaChip,
} from "../_internal/EntityActions";
import type {
  JobEntityData,
  JobEntityLocation,
  JobEntityProps,
} from "./JobEntity.types";

// Displays the job's freshness ("last seen from a source"), falling back to the original
// posting date when freshness is unavailable. Row layout shows an absolute date.
function formatFreshnessDate(
  value: Date | string | undefined,
  layout: "card" | "row",
): string {
  if (!value) return "Recently updated";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently updated";

  if (layout === "row") {
    return format(date, "MMM dd, yyyy");
  }
  if (isToday(date)) return "Updated today";
  if (isYesterday(date)) return "Updated yesterday";
  return `Updated ${formatDistanceToNow(date, { addSuffix: true })}`;
}

function formatLocationLabel(loc: JobEntityLocation | undefined): string {
  if (!loc) return "";
  return [loc.city, loc.state].filter(Boolean).join(", ");
}

export function JobEntity({
  job,
  layout = "card",
  density = "comfortable",
  actions,
  onApply,
  onContactHiringManager,
  onViewDetails,
  onCardClick,
  trackingContext,
  badge,
  className,
}: JobEntityProps) {
  const { trackEvent } = useEventTracking();
  const { trackJobView, trackJobApply, trackClick } = useEnhancedTracking();
  const router = useRouter();
  const pathname = usePathname();

  const cardRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const viewedRef = useRef(false);
  const blockCardClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isBlockingCardClick, setIsBlockingCardClick] = useState(false);
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);

  // Resolve action flags with layout-aware defaults.
  const resolvedActions = {
    apply: actions?.apply ?? true,
    contactHiringManager: actions?.contactHiringManager ?? false,
    viewDetails: actions?.viewDetails ?? false,
    save: actions?.save ?? false,
  };

  const isCompact = density === "compact";
  const isRow = layout === "row";

  const locations = job.locations.length > 0 ? job.locations : [{}];
  const currentLocation = locations[Math.min(selectedLocationIndex, locations.length - 1)];
  const hasMultipleLocations = locations.length > 1;

  // The "active" Job object used for external URL, id, tracking — swapped per selected location.
  const activeSourceJob = currentLocation?.sourceJob ?? job.sourceJob;
  const cpc = activeSourceJob?.cpc ?? job.cpc ?? 0;
  const hasCPC = cpc > 0;
  const externalUrl =
    activeSourceJob?.job_url ||
    activeSourceJob?.click_url ||
    job.externalUrl ||
    undefined;

  const isAuthenticatedApp =
    pathname?.startsWith("/dashboard") || pathname?.startsWith("/d");
  const jobDetailBasePath = isAuthenticatedApp ? "/d/job" : "/job";
  const jobDetailPath = generateJobDetailPath(
    activeSourceJob ?? {
      title: job.title,
      company: job.company.name,
      location: {
        city: currentLocation?.city,
        state: currentLocation?.state,
      },
      job_id: job.id,
      id: job.id,
    },
    jobDetailBasePath,
  );

  const locationLabel = formatLocationLabel(currentLocation);
  const postedLabel = formatFreshnessDate(job.lastSeenAt ?? job.postedAt, layout);
  const descriptionPreview = job.description ? stripHtml(job.description) : "";
  const badgeNode = badge ?? (job.badgeLabel ? job.badgeLabel : null);

  /* ----------------------- GetContactsButton dialog state ----------------------- */
  useEffect(() => {
    return () => {
      if (blockCardClickTimeoutRef.current) {
        clearTimeout(blockCardClickTimeoutRef.current);
      }
    };
  }, []);

  const handleContactsDialogStateChange = useCallback((open: boolean) => {
    if (blockCardClickTimeoutRef.current) {
      clearTimeout(blockCardClickTimeoutRef.current);
      blockCardClickTimeoutRef.current = null;
    }
    if (open) {
      setIsBlockingCardClick(true);
      return;
    }
    blockCardClickTimeoutRef.current = setTimeout(() => {
      setIsBlockingCardClick(false);
      blockCardClickTimeoutRef.current = null;
    }, 250);
  }, []);

  /* ------------------------- Location dropdown handling ------------------------ */
  useEffect(() => {
    if (!isLocationDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLocationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLocationDropdownOpen]);

  const handleLocationSelect = useCallback(
    (index: number) => {
      const fromLoc = currentLocation;
      const toLoc = locations[index];
      setSelectedLocationIndex(index);
      setIsLocationDropdownOpen(false);
      trackEvent({
        event_type: EventType.CLICK,
        element_id: "grouped-job-location-select",
        event_details: {
          job_title: job.title,
          company: job.company.name,
          from_location: formatLocationLabel(fromLoc),
          to_location: formatLocationLabel(toLoc),
          context: trackingContext,
        },
      });
    },
    [currentLocation, locations, trackEvent, job.title, job.company.name, trackingContext],
  );

  /* ------------------------------- View tracking ------------------------------- */
  useEffect(() => {
    if (isRow) return;
    if (typeof IntersectionObserver === "undefined") return;
    const id = job.id || activeSourceJob?.id || activeSourceJob?.job_id || "";
    const element = document.getElementById(`job-card-${id}`);
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          trackJobView(
            {
              jobId: String(id),
              jobTitle: job.title,
              jobCompany: job.company.name,
              jobLocation: locationLabel,
            },
            {
              componentName: "JobEntity",
              pageSection: "job_listings",
            },
          );
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [
    isRow,
    job.id,
    job.title,
    job.company.name,
    locationLabel,
    trackJobView,
    activeSourceJob?.id,
    activeSourceJob?.job_id,
  ]);

  /* ---------------------------------- Handlers ---------------------------------- */
  const openExternal = useCallback(() => {
    if (externalUrl) window.open(externalUrl, "_blank");
  }, [externalUrl]);

  const handleCardClick = useCallback(
    (event: React.MouseEvent) => {
      if (isBlockingCardClick) {
        event.stopPropagation();
        return;
      }
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          'button, a, input, textarea, select, [role="button"], [data-prevent-card-click="true"]',
        )
      ) {
        return;
      }

      if (onCardClick) {
        onCardClick(job);
        return;
      }

      trackEvent({
        event_type: EventType.JOB_VIEW,
        element_id: activeSourceJob?.job_id || job.id,
        event_details: {
          job_title: job.title,
          company: job.company.name,
          location: currentLocation?.city,
          context: trackingContext,
          action: "title_click",
          has_cpc: hasCPC,
          is_grouped: hasMultipleLocations,
          total_locations: locations.length,
        },
      });

      if (!hasCPC) {
        trackClick(
          {
            elementId: `job-card-${job.id}`,
            elementText: "Job Card",
            linkUrl: jobDetailPath,
          },
          {
            componentName: "JobEntity",
            pageSection: "job_listings",
            jobId: job.id,
            jobTitle: job.title,
          },
        );
        router.push(jobDetailPath);
      } else {
        openExternal();
      }
    },
    [
      isBlockingCardClick,
      onCardClick,
      job,
      trackEvent,
      activeSourceJob?.job_id,
      currentLocation?.city,
      trackingContext,
      hasCPC,
      hasMultipleLocations,
      locations.length,
      trackClick,
      jobDetailPath,
      router,
      openExternal,
    ],
  );

  const handleApplyClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      trackEvent({
        event_type: EventType.JOB_APPLY,
        element_id: activeSourceJob?.job_id || job.id,
        event_details: {
          job_title: job.title,
          company: job.company.name,
          location: currentLocation?.city,
          context: trackingContext,
          action: "apply_click",
          is_grouped: hasMultipleLocations,
          total_locations: locations.length,
        },
      });

      trackJobApply(
        {
          jobId: job.id,
          jobTitle: job.title,
          jobCompany: job.company.name,
          jobLocation: locationLabel,
        },
        {
          componentName: "JobEntity",
          pageSection: "job_listings",
        },
      );

      if (onApply) {
        onApply(job);
        return;
      }
      setIsApplyDialogOpen(true);
    },
    [
      trackEvent,
      trackJobApply,
      onApply,
      job,
      activeSourceJob?.job_id,
      currentLocation?.city,
      trackingContext,
      hasMultipleLocations,
      locations.length,
      locationLabel,
    ],
  );

  const handleViewDetailsClick = useCallback(() => {
    trackClick(
      {
        elementId: `job-details-${job.id}`,
        elementText: "View Details",
        linkUrl: jobDetailPath,
      },
      {
        componentName: "JobEntity",
        pageSection: "job_listings",
        jobId: job.id,
        jobTitle: job.title,
      },
    );
    if (onViewDetails) onViewDetails(job);
  }, [trackClick, onViewDetails, job, jobDetailPath]);

  const handleContactHiringManagerClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (onContactHiringManager) onContactHiringManager(job);
    },
    [onContactHiringManager, job],
  );

  /* --------------------------------- Row layout -------------------------------- */
  if (isRow) {
    return (
      <div
        id={`job-card-${job.id}`}
        className={cn(
          "group flex items-center gap-3 border-b border-border bg-card px-3 py-2 transition-colors hover:bg-background/40",
          onCardClick && "cursor-pointer",
          className,
        )}
        onClick={onCardClick ? handleCardClick : undefined}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-border bg-background p-1">
          <SafeImage
            src={job.company.logoUrl || ""}
            alt={`${job.company.name} logo`}
            width={32}
            height={32}
            className="max-h-full max-w-full object-contain"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-baseline gap-2">
            <EntityTitle
              size="sm"
              truncate
              href={jobDetailPath}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetailsClick();
              }}
            >
              {job.title}
            </EntityTitle>
            <EntitySubtitle as="span" tone="muted" truncate>
              {job.company.name}
            </EntitySubtitle>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {locationLabel && (
              <span className="text-caption inline-flex items-center gap-1 text-muted-foreground">
                <MapPinIcon className="h-3.5 w-3.5" />
                {locationLabel}
                {hasMultipleLocations && ` (+${locations.length - 1})`}
              </span>
            )}
            <span className="text-caption inline-flex items-center gap-1 text-muted-foreground">
              <CalendarIcon className="h-3.5 w-3.5" />
              {postedLabel}
            </span>
          </div>
        </div>
        <div className={JOB_ACTION_BUTTON_ROW_CLASS}>
          {resolvedActions.contactHiringManager && (
            <GetContactsButton
              type="job"
              companyName={job.company.name}
              jobTitle={job.title}
              jobLocation={locationLabel}
              jobUrl={externalUrl}
              jobId={activeSourceJob?.job_id || job.id}
              jobDescription={job.description}
              jobContextData={
                activeSourceJob as unknown as Record<string, unknown> | undefined
              }
              size="sm"
              variant="default"
              className={`${JOB_ACTION_BUTTON_COMPACT_CLASS} whitespace-nowrap bg-[var(--burgundy-core)] text-white hover:bg-[var(--burgundy-core)]/90`}
              onClick={handleContactHiringManagerClick}
              onDialogOpenChange={handleContactsDialogStateChange}
            >
              Contact
            </GetContactsButton>
          )}
          {resolvedActions.apply && (
            <EntityActionButton
              variant="secondary"
              size="sm"
              icon={ArrowTopRightOnSquareIcon}
              iconPosition="end"
              onClick={handleApplyClick}
              aria-label={`Apply for ${job.title} at ${job.company.name}`}
              className={JOB_ACTION_BUTTON_COMPACT_CLASS}
            >
              Apply
            </EntityActionButton>
          )}
        </div>
        <ApplyComparisonDialog
          open={isApplyDialogOpen}
          onOpenChange={setIsApplyDialogOpen}
          jobUrl={externalUrl}
          jobTitle={job.title}
          companyName={job.company.name}
          jobLocation={locationLabel}
          jobId={activeSourceJob?.job_id || job.id}
          jobDescription={job.description}
          jobContextData={
            activeSourceJob as unknown as Record<string, unknown> | undefined
          }
        />
      </div>
    );
  }

  /* --------------------------------- Card layout -------------------------------- */
  const logoBoxSize = isCompact
    ? "h-10 w-10 sm:h-12 sm:w-12"
    : "h-14 w-14 sm:h-16 sm:w-16";
  const logoPx = isCompact ? 32 : 48;

  return (
    <div
      id={`job-card-${job.id}`}
      className={cn("group relative hover:z-10", className)}
    >
      <Card
        ref={cardRef}
        brutalist={false}
        className={cn(
          "max-w-none cursor-pointer border border-border bg-card transition-all duration-200 hover:border-[3px] hover:border-[var(--burgundy-core)] hover:shadow-[var(--shadow-warm-sm)]",
          isLocationDropdownOpen && "z-10",
        )}
        onClick={handleCardClick}
      >
        <CardContent
          className={cn(
            "pt-4",
            isCompact ? "p-3 py-3" : "p-3 py-4 sm:p-4",
          )}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              <div
                className={cn(
                  "flex items-center justify-center border border-border bg-background p-2",
                  logoBoxSize,
                )}
              >
                <SafeImage
                  src={job.company.logoUrl || ""}
                  alt={`${job.company.name} logo`}
                  width={logoPx}
                  height={logoPx}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              {/* Title + company */}
              <div className="w-full">
                <EntityTitle
                  size={isCompact ? "sm" : "md"}
                  lineClamp={2}
                  href={jobDetailPath}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetailsClick();
                  }}
                >
                  {job.title}
                </EntityTitle>
                <EntitySubtitle tone="muted" lineClamp={1} className="mt-1.5">
                  {job.company.name}
                </EntitySubtitle>
              </div>

              {/* Description preview */}
              {!isCompact && descriptionPreview && (
                <p className="mt-2 line-clamp-2 hidden w-full text-body-sm text-muted-foreground sm:block">
                  {descriptionPreview.substring(0, 140)}...
                </p>
              )}

              {/* Meta + actions */}
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {badgeNode ? <EntityBadge>{badgeNode}</EntityBadge> : null}

                    {/* Location (dropdown if multiple) */}
                    <div className="relative" ref={dropdownRef}>
                      {hasMultipleLocations ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsLocationDropdownOpen((o) => !o);
                          }}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 border px-2.5 py-2 text-left transition-all sm:w-auto sm:max-w-full",
                            "text-body-sm font-medium",
                            isLocationDropdownOpen
                              ? "border-primary/40 bg-primary/5 text-foreground"
                              : "border-border/60 bg-background text-foreground hover:border-primary/40 hover:bg-primary/5",
                          )}
                        >
                          <span className="flex min-w-0 items-center gap-1.5">
                            <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                            <span className="truncate">{locationLabel}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1 border-l border-primary/30 pl-2 text-primary">
                            <span className="text-overline">
                              +{locations.length - 1}
                            </span>
                            {isLocationDropdownOpen ? (
                              <ChevronUpIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDownIcon className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      ) : (
                        <EntityMetaChip icon={MapPinIcon}>
                          {locationLabel || "Location TBD"}
                        </EntityMetaChip>
                      )}

                      <AnimatePresence>
                        {isLocationDropdownOpen && hasMultipleLocations && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden border border-border bg-background shadow-lg sm:right-auto sm:min-w-[220px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="max-h-60 overflow-y-auto">
                              {locations.map((location, index) => (
                                <button
                                  type="button"
                                  key={location.key ?? index}
                                  onClick={() => handleLocationSelect(index)}
                                  className={cn(
                                    "flex w-full items-center gap-2 px-4 py-2.5 text-left text-body-sm transition-colors",
                                    index === selectedLocationIndex
                                      ? "bg-primary/10 text-primary"
                                      : "text-foreground hover:bg-muted",
                                  )}
                                >
                                  <MapPinIcon className="h-4 w-4 shrink-0" />
                                  <span className="truncate">
                                    {formatLocationLabel(location)}
                                  </span>
                                  {index === selectedLocationIndex && (
                                    <span className="ml-auto text-primary">
                                      {"\u2713"}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <EntityMetaChip icon={CalendarIcon}>{postedLabel}</EntityMetaChip>

                    {!isCompact && job.categoryLabel && (
                      <span className="text-overline hidden border border-primary/20 bg-primary/5 px-2 py-0.5 text-primary sm:inline-flex">
                        <span className="max-w-[150px] truncate">
                          {job.categoryLabel}
                        </span>
                      </span>
                    )}
                  </div>

                  <div className={cn(JOB_ACTION_BUTTON_ROW_CLASS, "sm:flex-nowrap")}>
                    {resolvedActions.contactHiringManager && (
                      <GetContactsButton
                        type="job"
                        companyName={job.company.name}
                        jobTitle={job.title}
                        jobLocation={locationLabel}
                        jobUrl={externalUrl}
                        jobId={activeSourceJob?.job_id || job.id}
                        jobDescription={job.description}
                        jobContextData={
                          activeSourceJob as unknown as
                            | Record<string, unknown>
                            | undefined
                        }
                        size={isCompact ? "sm" : "default"}
                        variant="default"
                        className={`${isCompact ? JOB_ACTION_BUTTON_COMPACT_CLASS : JOB_ACTION_BUTTON_CLASS} whitespace-nowrap border border-[var(--burgundy-core)] bg-[var(--burgundy-core)] text-white transition-all duration-100 hover:bg-[var(--burgundy-core)]/90`}
                        style={{ boxShadow: "var(--shadow-warm-sm)" }}
                        onClick={handleContactHiringManagerClick}
                        onDialogOpenChange={handleContactsDialogStateChange}
                      >
                        Contact Hiring Manager
                      </GetContactsButton>
                    )}
                    {resolvedActions.apply && (
                      <EntityActionButton
                        variant="secondary"
                        size={isCompact ? "sm" : "md"}
                        icon={ArrowTopRightOnSquareIcon}
                        iconPosition="end"
                        onClick={handleApplyClick}
                        aria-label={`Apply for ${job.title} at ${job.company.name}`}
                        className={isCompact ? JOB_ACTION_BUTTON_COMPACT_CLASS : JOB_ACTION_BUTTON_CLASS}
                      >
                        Apply Normally
                      </EntityActionButton>
                    )}
                  </div>
              </div>

              {resolvedActions.viewDetails && (
                <div className="mt-3">
                  <Link
                    href={jobDetailPath}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetailsClick();
                    }}
                  >
                    <EntityActionButton variant="secondary" size="sm">
                      View Details
                    </EntityActionButton>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ApplyComparisonDialog
        open={isApplyDialogOpen}
        onOpenChange={setIsApplyDialogOpen}
        jobUrl={externalUrl}
        jobTitle={job.title}
        companyName={job.company.name}
        jobLocation={locationLabel}
        jobId={activeSourceJob?.job_id || job.id}
        jobDescription={job.description}
        jobContextData={
          activeSourceJob as unknown as Record<string, unknown> | undefined
        }
      />
    </div>
  );
}

export default JobEntity;
